/* eslint-disable import/prefer-default-export */
import {
  FollowItem,
  FollowsApi,
  GetFollowsForMember200Response,
} from '@deckee/api-app-main';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { callEndpoint } from '../../../backend';
import apiConfiguration from '../../../backend/apiConfiguration';
import { remove, set } from '../../../local-storage';
import { selectCurrentMemberId } from '../backend-service';

const api = new FollowsApi(apiConfiguration);

const followAction = (follows: FollowItem[]) => ({
  payload: follows,
});

export const loadingSignalFollows = createAction(
  '[Follows] Loading signal follows',
  (isLoading: boolean) => ({ payload: isLoading }),
);

export const setFollows = createAction('[Follows] set follows', followAction);

export const addFollows = createAction('[Follows] add follows', followAction);

export const followMember = createAsyncThunk<Promise<void>, string>(
  '[Follows] fetch all follows for member',
  async (followeeId, { rejectWithValue, getState, dispatch }) => {
    const memberId = selectCurrentMemberId(getState());
    let follow: FollowItem | undefined;
    if (!memberId) {
      rejectWithValue('No current member');
    } else {
      try {
        const { data } = await api.createFollowsForMember({
          memberId,
          createFollowsForMemberRequest: {
            followeeId,
          },
        });
        follow = data;
      } catch (e: any) {
        rejectWithValue(e?.message);
      }

      if (follow) {
        dispatch(addFollows([follow]));
      }
    }
  },
);

export const removeFollow = createAction(
  '[Follows] remove follow',
  (followId: string) => ({
    payload: followId,
  }),
);

export const unfollow = createAsyncThunk<void, string>(
  '[Follows] unfollow',
  async (followId, { dispatch, rejectWithValue }) => {
    try {
      await callEndpoint(callEndpoint.endpoints.unfollowMember, {
        ':followId': followId,
      });

      dispatch(removeFollow(followId));
    } catch (e: any) {
      throw rejectWithValue(e?.message);
    }
  },
);

export const fetchFollowsForCurrentMember = createAsyncThunk<void, void>(
  '[Follows] fetch all follows for member',
  async (_, { dispatch, rejectWithValue, getState }) => {
    dispatch(loadingSignalFollows(true));

    const memberId = selectCurrentMemberId(getState());
    let follows: FollowItem[] = [];

    if (!memberId) {
      rejectWithValue('No current member');
    } else {
      // TODO: This ideally is a generic of callEndpoint when it gets converted to TS
      try {
        follows = await callEndpoint<GetFollowsForMember200Response>(
          callEndpoint.endpoints.getFollowsForMember,
          {
            ':memberId': memberId,
          },
        );
      } catch (e: any) {
        rejectWithValue(e?.message);
      }

      dispatch(setFollows(follows));
      dispatch(loadingSignalFollows(false));
    }
  },
);

export const setInvitationCode = createAction(
  '[Follows] add invitation code',
  (invitationCode: string) => {
    // We are setting the invitation code in local storage to account for the case where a user shuts the app during the login/signup process
    set('follows', 'invitationCode', invitationCode);

    return {
      payload: invitationCode,
    };
  },
);

export const clearInvitationCode = createAction(
  '[Follows] clear existing invitation code',
  () => {
    // We need to make sure we also clear the local storage inline with it being added in the setInvitationCode action
    remove('follows', 'invitationCode');
    return { payload: null };
  },
);
